import { 
  createChart, 
  ColorType, 
  CrosshairMode, 
  AreaSeries, 
  LineSeries, 
  CandlestickSeries, 
  HistogramSeries 
} from 'lightweight-charts';
import { useEffect, useRef } from 'react';

/**
 * TradingView Lightweight Charts component for displaying price charts.
 * Supports candlestick, line, and area chart types.
 */
export const PriceChart = ({ 
  data = [], 
  symbol = '',
  chartType = 'area', // 'candlestick', 'line', 'area'
  height = 400,
  showVolume = false,
  theme = 'dark'
}) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);

  const colors = theme === 'dark' ? {
    background: '#0f172a',
    text: '#94a3b8',
    grid: '#1e293b',
    upColor: '#10b981',
    downColor: '#ef4444',
    lineColor: '#10b981',
    areaTop: 'rgba(16, 185, 129, 0.4)',
    areaBottom: 'rgba(16, 185, 129, 0.0)',
    crosshair: '#64748b',
    border: '#334155'
  } : {
    background: '#ffffff',
    text: '#1e293b',
    grid: '#f1f5f9',
    upColor: '#10b981',
    downColor: '#ef4444',
    lineColor: '#10b981',
    areaTop: 'rgba(16, 185, 129, 0.4)',
    areaBottom: 'rgba(16, 185, 129, 0.0)',
    crosshair: '#94a3b8',
    border: '#e2e8f0'
  };

  useEffect(() => {
    if (!chartContainerRef.current || data.length === 0) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { type: ColorType.Solid, color: colors.background },
        textColor: colors.text,
      },
      grid: {
        vertLines: { color: colors.grid },
        horzLines: { color: colors.grid },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: colors.crosshair,
          width: 1,
          style: 2,
          labelBackgroundColor: colors.background,
        },
        horzLine: {
          color: colors.crosshair,
          width: 1,
          style: 2,
          labelBackgroundColor: colors.background,
        },
      },
      rightPriceScale: {
        borderColor: colors.border,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      timeScale: {
        borderColor: colors.border,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time) => {
          // Custom time formatter to avoid locale issues
          if (typeof time === 'string') {
            return time;
          }
          const date = new Date(time * 1000);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        },
      },
      localization: {
        locale: 'en-US',
        dateFormat: 'yyyy-MM-dd',
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    chartRef.current = chart;

    // Add series based on chart type (v5 API)
    let series;
    
    if (chartType === 'candlestick') {
      series = chart.addSeries(CandlestickSeries, {
        upColor: colors.upColor,
        downColor: colors.downColor,
        borderUpColor: colors.upColor,
        borderDownColor: colors.downColor,
        wickUpColor: colors.upColor,
        wickDownColor: colors.downColor,
      });
      
      // Transform data for candlestick
      const candleData = data.map(d => ({
        time: d.time,
        open: d.open || d.value,
        high: d.high || d.value,
        low: d.low || d.value,
        close: d.close || d.value,
      }));
      series.setData(candleData);
      
    } else if (chartType === 'line') {
      series = chart.addSeries(LineSeries, {
        color: colors.lineColor,
        lineWidth: 2,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 4,
        crosshairMarkerBorderColor: colors.lineColor,
        crosshairMarkerBackgroundColor: colors.background,
      });
      
      const lineData = data.map(d => ({
        time: d.time,
        value: d.close || d.value,
      }));
      series.setData(lineData);
      
    } else {
      // Area chart (default) - v5 API
      series = chart.addSeries(AreaSeries, {
        lineColor: colors.lineColor,
        topColor: colors.areaTop,
        bottomColor: colors.areaBottom,
        lineWidth: 2,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 4,
      });
      
      const areaData = data.map(d => ({
        time: d.time,
        value: d.close || d.value,
      }));
      series.setData(areaData);
    }

    seriesRef.current = series;

    // Add volume if requested and data available (v5 API)
    if (showVolume && data.some(d => d.volume)) {
      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: colors.upColor,
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
      });
      volumeSeries.priceScale().applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
      });

      const volumeData = data.map(d => ({
        time: d.time,
        value: d.volume || 0,
        color: (d.close || d.value) >= (d.open || d.value) 
          ? 'rgba(16, 185, 129, 0.5)' 
          : 'rgba(239, 68, 68, 0.5)',
      }));
      volumeSeries.setData(volumeData);
    }

    // Fit content
    chart.timeScale().fitContent();

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [data, chartType, height, showVolume, theme]);

  return (
    <div className="relative">
      {symbol && (
        <div className="absolute top-2 left-2 z-10 px-2 py-1 bg-slate-800/80 rounded text-sm text-white font-medium">
          {symbol}
        </div>
      )}
      <div 
        ref={chartContainerRef} 
        className="w-full rounded-lg overflow-hidden"
        data-testid="price-chart"
      />
    </div>
  );
};

/**
 * Mini sparkline chart for compact displays.
 */
export const SparklineChart = ({ 
  data = [], 
  width = 100, 
  height = 40,
  color = '#10b981',
  showChange = false
}) => {
  const chartContainerRef = useRef(null);

  useEffect(() => {
    if (!chartContainerRef.current || data.length === 0) return;

    const chart = createChart(chartContainerRef.current, {
      width: width,
      height: height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: 'transparent',
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      rightPriceScale: { visible: false },
      timeScale: { visible: false },
      handleScroll: false,
      handleScale: false,
      crosshair: {
        vertLine: { visible: false },
        horzLine: { visible: false },
      },
    });

    // Determine color based on price change
    const firstValue = data[0]?.value || 0;
    const lastValue = data[data.length - 1]?.value || 0;
    const isPositive = lastValue >= firstValue;
    const lineColor = showChange 
      ? (isPositive ? '#10b981' : '#ef4444')
      : color;

    const series = chart.addSeries(AreaSeries, {
      lineColor: lineColor,
      topColor: `${lineColor}40`,
      bottomColor: `${lineColor}00`,
      lineWidth: 1,
      crosshairMarkerVisible: false,
    });

    series.setData(data);
    chart.timeScale().fitContent();

    return () => chart.remove();
  }, [data, width, height, color, showChange]);

  return (
    <div 
      ref={chartContainerRef} 
      style={{ width, height }}
      data-testid="sparkline-chart"
    />
  );
};

export default PriceChart;
